import React, { useState, useEffect } from "react";
import axios from "axios";
import * as XLSX from "xlsx";
import { useNavigate } from "react-router-dom";
import { BASE_URL } from "../config";
import { getAuthHeaders } from "../services/useAuth";

const TODAY = new Date().toISOString().split("T")[0];

/* ================= PARSE REPORT ================= */

function parseReport(data) {
  const raw = Array.isArray(data) ? data[0] : data;
  if (!raw) return { total_visits: 0, total_locations: 0, visits: [] };
  return {
    total_visits: raw.total_visits || 0,
    total_locations: raw.total_locations || 0,
    visits: Array.isArray(raw.visits) ? raw.visits : [],
  };
}

/* ================= COMPONENT ================= */

export default function EmployeePage() {
  const navigate = useNavigate();

  const [states, setStates] = useState([]);
  const [state, setState] = useState("");
  // Report type options matching the API: Wifi | Mock | Android LS | Multiple Device
  const [type, setType] = useState("Mock");
  const [date, setDate] = useState(TODAY);

  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  /* ================= FETCH STATES ================= */

  useEffect(() => {
    const fetchStates = async () => {
      try {
        const res = await axios.get(`${BASE_URL}/api/states`, {
          headers: getAuthHeaders(),
        });
        const list = Array.isArray(res.data) ? res.data : [];
        setStates(list);
        if (list.length) setState(list[0].name);
      } catch (err) {
        console.error("States API Error:", err);
        setError("Failed to load states.");
      }
    };
    fetchStates();
  }, []);

  /* ================= FETCH DATA ================= */

  const fetchEmployees = async () => {
    if (!state || !date) {
      alert("Please select State and Date");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const selectedState = states.find((s) => s.name === state);

      const res = await axios.get(`${BASE_URL}/api/employee-location-summary`, {
        params: {
          report_type: type,
          state_id: selectedState?.id,
          track_date: date,
        },
        headers: getAuthHeaders(),
      });

      const list = Array.isArray(res.data?.data) ? res.data.data : [];

      const formatted = list.map((emp) => ({
        id: Number(emp.employee),
        emp_id: emp.emp_id,
        first_name: emp.employee_name,
        last_name: "",
        division: emp.division,
        designation: emp.designation,
        state: emp.state,
        city: emp.city,
        manager: emp.manager || "",
        report: {
          total_visits: emp.visits || 0,
          total_locations: emp.devices || 0,
          visits: [],
        },
      }));

      setEmployees(formatted);
    } catch (err) {
      console.error("Summary API Error:", err);
      setError("Failed to load employee summary");
    }

    setLoading(false);
  };

  /* ================= HANDLE VISIT CLICK ================= */

  const handleVisitClick = (emp) => {
    const params = new URLSearchParams({
      employee_id: emp.id, // IMPORTANT
      employee_name: `${emp.first_name} ${emp.last_name || ""}`,
      track_date: date,
    });

    navigate(`/reporting/location-history?${params.toString()}`);
  };
  /* ================= EXPORT ================= */

  const exportExcel = () => {
    if (!employees.length) {
      alert("No data to export");
      return;
    }

    const rows = employees.map((e, i) => ({
      "S.No": i + 1,
      "Emp ID": e.emp_id || e.employee || "",
      Employee: `${e.first_name || ""} ${e.last_name || ""}`,
      Division: e.division || "",
      Designation: e.designation || "",
      State: e.state || "",
      City: e.city || "",
      Manager: e.manager || "",
      "Total Visits": e.report?.total_visits ?? 0,
      "Total Locations": e.report?.total_locations ?? 0,
      "Visited Retailers": (e.report?.visits || [])
        .map((v) => v.retailer_name)
        .join(", "),
    }));

    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Employee Report");
    XLSX.writeFile(wb, `Employee_Report_${state}_${date}.xlsx`);
  };

  /* ================= UI ================= */

  return (
    <div className="p-4 bg-gray-100 min-h-screen">
      {/* FILTER */}
      <div className="bg-white border border-gray-400 p-4 mb-4">
        <h2 className="text-sm font-semibold mb-3 text-center">
          Fake Reporting Wifi Mock GLS Table
        </h2>

        <div className="flex justify-center">
          <div className="w-full max-w-6xl space-y-4">
            <div className="flex gap-16">
              {/* STATE */}
              <div className="flex items-center gap-3 flex-1">
                <label className="text-sm w-16">State</label>
                <select
                  value={state}
                  onChange={(e) => setState(e.target.value)}
                  className="border border-gray-400 px-2 py-1 w-full text-sm"
                >
                  {states.map((s) => (
                    <option key={s.id} value={s.name}>
                      {s.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* TYPE — all 4 report types */}
              <div className="flex items-center gap-3 flex-1">
                <label className="text-sm w-16">Type</label>
                <select
                  value={type}
                  onChange={(e) => setType(e.target.value)}
                  className="border border-gray-400 px-2 py-1 w-full text-sm"
                >
                  <option value="Wifi">Wifi</option>
                  <option value="Mock">Mock</option>
                  <option value="Android LS">Android LS</option>
                  <option value="Multiple Device">Multiple Device</option>
                </select>
              </div>
            </div>

            {/* DATE */}
            <div className="flex gap-16">
              <div className="flex items-center gap-3 flex-1">
                <label className="text-sm w-16">Date</label>
                <input
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  className="border border-gray-400 px-2 py-1 w-full text-sm"
                />
              </div>

              <div className="flex items-center gap-3 flex-1">
                <span className="w-16"></span>
                <button
                  onClick={fetchEmployees}
                  disabled={loading}
                  className="bg-blue-500 hover:bg-blue-600 text-white px-10 py-1.5 text-sm"
                >
                  {loading ? "Loading..." : "Go"}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ERROR */}
      {error && <div className="text-red-600 text-sm mb-4">{error}</div>}

      {/* TABLE */}
      <div className="bg-white border border-gray-400">
        <div className="bg-gray-100 border-b border-gray-400 px-4 py-2 text-sm font-semibold">
          {state} — Employee List — Type: {type}
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead className="bg-cyan-100">
              <tr>
                {[
                  "S.No",
                  "Emp ID",
                  "Employee",
                  "Division",
                  "Designation",
                  "State",
                  "City",
                  "Manager",
                  "Total Visits",
                  "Total Locations",
                  "Visited Retailers",
                ].map((h) => (
                  <th key={h} className="border border-gray-400 px-2 py-1">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {employees.length === 0 ? (
                <tr>
                  <td
                    colSpan={11}
                    className="border px-2 py-6 text-center text-gray-400"
                  >
                    {loading
                      ? "Loading data..."
                      : "No data. Select filters and click Go."}
                  </td>
                </tr>
              ) : (
                employees.map((e, i) => (
                  <tr key={e.id} className="hover:bg-gray-50">
                    <td className="border px-2 py-1 text-center">{i + 1}</td>
                    <td className="border px-2 py-1">
                      {e.emp_id || e.employee}
                    </td>
                    <td className="border px-2 py-1">
                      {e.first_name} {e.last_name}
                    </td>
                    <td className="border px-2 py-1">{e.division}</td>
                    <td className="border px-2 py-1">{e.designation}</td>
                    <td className="border px-2 py-1">{e.state}</td>
                    <td className="border px-2 py-1">{e.city}</td>
                    <td className="border px-2 py-1">{e.manager}</td>

                    {/* CLICKABLE VISIT COUNT — navigates to LocationHistoryTime */}
                    <td className="border px-2 py-1 text-center">
                      <button
                        onClick={() => handleVisitClick(e)}
                        className="text-blue-600 hover:text-blue-800 hover:underline font-semibold cursor-pointer"
                        title="Click to view location history"
                      >
                        {e.report?.total_visits ?? 0}
                      </button>
                    </td>

                    <td className="border px-2 py-1 text-center text-blue-600">
                      {e.report?.total_locations ?? 0}
                    </td>

                    <td className="border px-2 py-1">
                      {(e.report?.visits || [])
                        .map((v) => v.retailer_name)
                        .join(", ")}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* FOOTER */}
        <div className="flex justify-center gap-4 p-4">
          <button
            onClick={() => window.print()}
            className="border px-4 py-1 text-sm bg-gray-200"
          >
            🖨 PRINT
          </button>
          <button
            onClick={exportExcel}
            className="px-4 py-1 text-sm bg-green-600 text-white"
          >
            📊 Export To Excel
          </button>
        </div>
      </div>
    </div>
  );
}
